import _styled, { css } from 'styled-components';
import React, { forwardRef, useRef, useEffect, useState } from 'react';
import { AvatarImage, AccountCircleIcon, useUserById, MoreVertIcon, CommentEditButton, CommentDeleteButton, useCommentSelectors, useCommentActions, CommentResolveButton, RefreshIcon, CheckIcon, useComment, CommentNewTextarea, CommentEditTextarea, CommentEditActions, useCommentById, CommentProvider, useCommentText, useCommentUser, useCommentsSelectors, CommentUserName, useCommentsActions, isCommentKey, getCommentKeyId, CommentNewSubmitButton, useCommentReplies, SCOPE_ACTIVE_COMMENT, useCommentAddButton, useCommentsResolved, useFloatingCommentsContentState, CommentsPositioner, useFloatingCommentsState } from '@udecode/plate-comments';
import { PlateButton, plateButtonCss, primaryButtonCss } from '@udecode/plate-ui-button';
import { ToolbarDropdown, floatingRootCss, ToolbarButton } from '@udecode/plate-ui-toolbar';
import { StyledLeaf, PortalBody } from '@udecode/plate-styled-components';
import { usePlateEditorRef, useEventPlateId, focusEditor } from '@udecode/plate-core';

const avatarRootCss = css(["", ";-webkit-tap-highlight-color:rgba(0,0,0,0);user-select:text;aspect-ratio:auto 32 / 32;"], {
  "fontWeight": "400",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "cursor": "default",
  "whiteSpace": "nowrap",
  "width": "2rem",
  "height": "2rem",
  "objectFit": "cover",
  "left": "0px",
  "display": "block",
  "position": "relative",
  "borderRadius": "9999px"
});
const avatarImageCss = css(["", " -webkit-tap-highlight-color:rgba(0,0,0,0);user-select:text;"], {
  "borderRadius": "9999px",
  "fontWeight": "400",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "cursor": "default",
  "whiteSpace": "nowrap",
  "height": "2rem",
  "width": "2rem"
});
const avatarAccountCircleCss = css(["", ";-webkit-tap-highlight-color:rgba(0,0,0,0);user-select:text;"], {
  "fontWeight": "400",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(107, 114, 128, var(--tw-text-opacity))",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "cursor": "default",
  "whiteSpace": "nowrap",
  "height": "2rem",
  "width": "2rem"
});
const PlateAvatar = ({
  userId
}) => {
  const user = useUserById(userId);
  if (!user) return null;
  return /*#__PURE__*/React.createElement(_StyledDiv$7, null, user.avatarUrl ? /*#__PURE__*/React.createElement(_StyledAvatarImage, {
    userId: userId
  }) : /*#__PURE__*/React.createElement(_StyledAccountCircleIcon, {
    viewBox: "0 0 24 24"
  }));
};

var _StyledDiv$7 = _styled("div").withConfig({
  displayName: "PlateAvatar___StyledDiv",
  componentId: "sc-jd7fqv-0"
})(["", ""], avatarRootCss);

var _StyledAvatarImage = _styled(AvatarImage).withConfig({
  displayName: "PlateAvatar___StyledAvatarImage",
  componentId: "sc-jd7fqv-1"
})(["", ""], avatarImageCss);

var _StyledAccountCircleIcon = _styled(AccountCircleIcon).withConfig({
  displayName: "PlateAvatar___StyledAccountCircleIcon",
  componentId: "sc-jd7fqv-2"
})(["", ""], avatarAccountCircleCss);

const PlateCommentMenuButton = () => {
  const isMenuOpen = useCommentSelectors().isMenuOpen();
  const setIsMenuOpen = useCommentActions().isMenuOpen();
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ToolbarDropdown, {
    control: /*#__PURE__*/React.createElement(_StyledPlateButton, null, /*#__PURE__*/React.createElement(_StyledMoreVertIcon, null)),
    open: isMenuOpen,
    onOpen: () => setIsMenuOpen(true),
    onClose: () => setIsMenuOpen(false)
  }, /*#__PURE__*/React.createElement(_StyledDiv$6, {
    $_css: [{
      "display": "flex",
      "flexDirection": "column",
      "position": "relative"
    }, floatingRootCss, css(["width:150px;"])]
  }, /*#__PURE__*/React.createElement(_StyledCommentEditButton, {
    $_css2: [plateButtonCss, {
      "justifyContent": "flex-start",
      "paddingLeft": "1rem",
      "paddingRight": "1rem",
      "paddingTop": "0.5rem",
      "paddingBottom": "0.5rem"
    }]
  }, "Edit comment"), /*#__PURE__*/React.createElement(_StyledCommentDeleteButton, {
    $_css3: [plateButtonCss, {
      "justifyContent": "flex-start",
      "paddingLeft": "1rem",
      "paddingRight": "1rem",
      "paddingTop": "0.5rem",
      "paddingBottom": "0.5rem"
    }]
  }, "Delete comment"))));
};

var _StyledPlateButton = _styled(PlateButton).withConfig({
  displayName: "PlateCommentMenuButton___StyledPlateButton",
  componentId: "sc-1x3epdp-0"
})({
  "padding": "0.25rem"
});

var _StyledMoreVertIcon = _styled(MoreVertIcon).withConfig({
  displayName: "PlateCommentMenuButton___StyledMoreVertIcon",
  componentId: "sc-1x3epdp-1"
})({
  "width": "1.5rem",
  "height": "1.5rem",
  "--tw-text-opacity": "1",
  "color": "rgba(107, 114, 128, var(--tw-text-opacity))"
});

var _StyledDiv$6 = _styled("div").withConfig({
  displayName: "PlateCommentMenuButton___StyledDiv",
  componentId: "sc-1x3epdp-2"
})(["", ""], p => p.$_css);

var _StyledCommentEditButton = _styled(CommentEditButton).withConfig({
  displayName: "PlateCommentMenuButton___StyledCommentEditButton",
  componentId: "sc-1x3epdp-3"
})(["", ""], p => p.$_css2);

var _StyledCommentDeleteButton = _styled(CommentDeleteButton).withConfig({
  displayName: "PlateCommentMenuButton___StyledCommentDeleteButton",
  componentId: "sc-1x3epdp-4"
})(["", ""], p => p.$_css3);

const PlateCommentResolveButton = () => {
  const comment = useComment();
  return /*#__PURE__*/React.createElement(_StyledCommentResolveButton, {
    $_css: [plateButtonCss, {
      "padding": "0.25rem"
    }]
  }, comment.isResolved ? /*#__PURE__*/React.createElement(_StyledRefreshIcon, null) : /*#__PURE__*/React.createElement(_StyledCheckIcon, null));
};

var _StyledCommentResolveButton = _styled(CommentResolveButton).withConfig({
  displayName: "PlateCommentResolveButton___StyledCommentResolveButton",
  componentId: "sc-1pzclgi-0"
})(["", ""], p => p.$_css);

var _StyledRefreshIcon = _styled(RefreshIcon).withConfig({
  displayName: "PlateCommentResolveButton___StyledRefreshIcon",
  componentId: "sc-1pzclgi-1"
})({
  "width": "1.5rem",
  "height": "1.5rem",
  "--tw-text-opacity": "1",
  "color": "rgba(107, 114, 128, var(--tw-text-opacity))"
});

var _StyledCheckIcon = _styled(CheckIcon).withConfig({
  displayName: "PlateCommentResolveButton___StyledCheckIcon",
  componentId: "sc-1pzclgi-2"
})({
  "width": "1.5rem",
  "height": "1.5rem",
  "--tw-text-opacity": "1",
  "color": "rgba(107, 114, 128, var(--tw-text-opacity))"
});

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

const commentTextareaCss = css(["", ";border:1px solid #dadce0;min-height:36px;outline-width:0;text-align:start;word-wrap:break-word;"], {
  "--tw-text-opacity": "1",
  "color": "rgba(31, 41, 55, var(--tw-text-opacity))",
  "lineHeight": "1.25rem",
  "height": "2.5rem",
  "borderRadius": "0.25rem",
  "boxSizing": "border-box",
  "fontSize": "0.875rem",
  "padding": "0.5rem",
  "display": "block",
  "margin": "0px",
  "overflowX": "hidden",
  "overflowY": "hidden",
  "resize": "none",
  "width": "100%",
  "cursor": "text"
});
const PlateCommentNewTextarea = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(_StyledCommentNewTextarea, _extends({}, props, {
    ref: ref
  }));
});
const PlateCommentEditTextarea = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/React.createElement(_StyledCommentEditTextarea, _extends({}, props, {
    ref: ref
  }));
});

var _StyledCommentNewTextarea = _styled(CommentNewTextarea).withConfig({
  displayName: "PlateCommentNewTextarea___StyledCommentNewTextarea",
  componentId: "sc-1psd4gr-0"
})(["", ""], commentTextareaCss);

var _StyledCommentEditTextarea = _styled(CommentEditTextarea).withConfig({
  displayName: "PlateCommentNewTextarea___StyledCommentEditTextarea",
  componentId: "sc-1psd4gr-1"
})(["", ""], commentTextareaCss);

const PlateCommentValue = () => {
  const textareaRef = useRef(null);
  useEffect(() => {
    const textarea = textareaRef.current;
    textarea.focus();
    const {
      length
    } = textarea.value;
    textarea.setSelectionRange(length, length);
  }, [textareaRef]);
  return /*#__PURE__*/React.createElement(_StyledDiv$5, null, /*#__PURE__*/React.createElement(PlateCommentEditTextarea, {
    ref: textareaRef
  }), /*#__PURE__*/React.createElement(_StyledDiv2$4, {
    $_css: [{
      "display": "flex",
      "> :not([hidden]) ~ :not([hidden])": {
        "--tw-space-x-reverse": 0,
        "marginRight": "calc(0.5rem * var(--tw-space-x-reverse))",
        "marginLeft": "calc(0.5rem * calc(1 - var(--tw-space-x-reverse)))"
      }
    }]
  }, /*#__PURE__*/React.createElement(_StyledCommentEditActionsSaveButton, {
    $_css2: [primaryButtonCss]
  }, "Save"), /*#__PURE__*/React.createElement(_StyledCommentEditActionsCancelButton, {
    $_css3: [plateButtonCss]
  }, "Cancel")));
};

var _StyledDiv$5 = _styled("div").withConfig({
  displayName: "PlateCommentValue___StyledDiv",
  componentId: "sc-g7t260-0"
})({
  "display": "flex",
  "flexDirection": "column",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": 0,
    "marginTop": "calc(0.5rem * calc(1 - var(--tw-space-y-reverse)))",
    "marginBottom": "calc(0.5rem * var(--tw-space-y-reverse))"
  }
});

var _StyledDiv2$4 = _styled("div").withConfig({
  displayName: "PlateCommentValue___StyledDiv2",
  componentId: "sc-g7t260-1"
})(["", ""], p => p.$_css);

var _StyledCommentEditActionsSaveButton = _styled(CommentEditActions.SaveButton).withConfig({
  displayName: "PlateCommentValue___StyledCommentEditActionsSaveButton",
  componentId: "sc-g7t260-2"
})(["", ""], p => p.$_css2);

var _StyledCommentEditActionsCancelButton = _styled(CommentEditActions.CancelButton).withConfig({
  displayName: "PlateCommentValue___StyledCommentEditActionsCancelButton",
  componentId: "sc-g7t260-3"
})(["", ""], p => p.$_css3);

const commentsHeaderCss = css(["", ";font-weight:normal;"], {
  "boxSizing": "content-box",
  "cursor": "default",
  "display": "flex",
  "alignItems": "center",
  "height": "2.5rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "margin": "0px",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "whiteSpace": "nowrap"
});
const commentHeaderInfoCss = css(["", ";font-weight:normal;align-items:start;"], {
  "cursor": "pointer",
  "display": "flex",
  "flexDirection": "column",
  "marginLeft": "0.5rem",
  "justifyContent": "center",
  "flexGrow": "1",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "overflow": "hidden",
  "textOverflow": "ellipsis",
  "whiteSpace": "nowrap"
});
const commentHeaderUserNameCss = css(["", ";color:rgba(60,64,67,1);"], {
  "cursor": "default",
  "alignSelf": "stretch",
  "fontWeight": "500",
  "height": "1rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "textAlign": "left",
  "letterSpacing": "0.025em",
  "overflow": "hidden",
  "textOverflow": "ellipsis",
  "whiteSpace": "nowrap"
});
const commentHeaderCreatedDateCss = css(["", ";color:rgba(60,64,67,1);"], {
  "fontSize": "0.75rem",
  "lineHeight": "1rem",
  "letterSpacing": "0.025em"
});
const commentTextCss = css(["", ";"], {
  "whiteSpace": "pre-wrap"
});

const PlateCommentContent = () => {
  const comment = useComment();
  const isReplyComment = !!comment.parentId;
  const commentText = useCommentText();
  const user = useCommentUser();
  const myUserId = useCommentsSelectors().myUserId();
  const editingValue = useCommentSelectors().editingValue();
  const isMyComment = myUserId === comment.userId;
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_StyledDiv$4, null, /*#__PURE__*/React.createElement(PlateAvatar, {
    userId: comment.userId
  }), /*#__PURE__*/React.createElement(_StyledDiv2$3, null, /*#__PURE__*/React.createElement(_StyledDiv3$2, null, user === null || user === void 0 ? void 0 : user.name), /*#__PURE__*/React.createElement(_StyledDiv4$2, null, new Date(comment.createdAt).toLocaleString())), isMyComment && /*#__PURE__*/React.createElement(_StyledDiv5$1, null, !isReplyComment ? /*#__PURE__*/React.createElement(PlateCommentResolveButton, null) : null, /*#__PURE__*/React.createElement(PlateCommentMenuButton, null))), /*#__PURE__*/React.createElement(_StyledDiv6, null, editingValue ? /*#__PURE__*/React.createElement(PlateCommentValue, null) : /*#__PURE__*/React.createElement(_StyledDiv7, null, commentText)));
};

const PlateComment = ({
  commentId
}) => {
  const comment = useCommentById(commentId);
  if (!comment) return null;
  return /*#__PURE__*/React.createElement(CommentProvider, {
    key: commentId,
    id: commentId
  }, /*#__PURE__*/React.createElement(PlateCommentContent, null));
};

var _StyledDiv$4 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv",
  componentId: "sc-1mvnoox-0"
})(["", ""], commentsHeaderCss);

var _StyledDiv2$3 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv2",
  componentId: "sc-1mvnoox-1"
})(["", ""], commentHeaderInfoCss);

var _StyledDiv3$2 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv3",
  componentId: "sc-1mvnoox-2"
})(["", ""], commentHeaderUserNameCss);

var _StyledDiv4$2 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv4",
  componentId: "sc-1mvnoox-3"
})(["", ""], commentHeaderCreatedDateCss);

var _StyledDiv5$1 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv5",
  componentId: "sc-1mvnoox-4"
})({
  "display": "flex",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-x-reverse": 0,
    "marginRight": "calc(0.25rem * var(--tw-space-x-reverse))",
    "marginLeft": "calc(0.25rem * calc(1 - var(--tw-space-x-reverse)))"
  }
});

var _StyledDiv6 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv6",
  componentId: "sc-1mvnoox-5"
})({
  "paddingLeft": "2.5rem"
});

var _StyledDiv7 = _styled("div").withConfig({
  displayName: "PlateComment___StyledDiv7",
  componentId: "sc-1mvnoox-6"
})(["", ""], commentTextCss);

const userHeaderRootCss = css(["", ";border-bottom:1px solid rgb(218,220,224);"], {
  "display": "flex",
  "flexDirection": "row",
  "alignItems": "center",
  "padding": "0.75rem",
  "borderTopLeftRadius": "0.5rem",
  "borderTopRightRadius": "0.5rem"
});
const userHeaderAvatarContainerCss = css(["", ";"], {
  "marginRight": "0.5rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgba(255, 255, 255, var(--tw-bg-opacity))",
  "width": "2rem",
  "height": "2rem",
  "flex": "none",
  "borderRadius": "9999px"
});
const userHeaderAssignedToContainerCss = css(["", ""], {
  "flex": "1 1 auto"
});
const userHeaderAssignedToTextCss = css(["", ";"], {
  "fontSize": "0.75rem",
  "lineHeight": "1rem"
});
const commentUserCss = css(["", ";"], {
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "fontWeight": "500"
});
const userHeaderActionsCss = css(["", ";"], {
  "flex": "none"
});
const PlateCommentHeader = () => {
  const comment = useComment();
  const myUserId = useCommentsSelectors().myUserId();
  const isMyUser = myUserId === comment.userId;
  return /*#__PURE__*/React.createElement(_StyledDiv$3, {
    $_css: [userHeaderRootCss, css(["color:", ";background-color:", ";"], isMyUser ? 'white' : 'rgb(60, 64, 67)', isMyUser ? '#1a73e8' : '#e8f0fe')]
  }, /*#__PURE__*/React.createElement(_StyledDiv2$2, null, /*#__PURE__*/React.createElement(PlateAvatar, {
    userId: comment.userId
  })), /*#__PURE__*/React.createElement(_StyledDiv3$1, null, /*#__PURE__*/React.createElement(_StyledDiv4$1, null, "Author"), /*#__PURE__*/React.createElement(_StyledCommentUserName, null)), /*#__PURE__*/React.createElement(_StyledDiv5, null, /*#__PURE__*/React.createElement(PlateCommentResolveButton, null)));
};

var _StyledDiv$3 = _styled("div").withConfig({
  displayName: "PlateCommentHeader___StyledDiv",
  componentId: "sc-ot8r15-0"
})(["", ""], p => p.$_css);

var _StyledDiv2$2 = _styled("div").withConfig({
  displayName: "PlateCommentHeader___StyledDiv2",
  componentId: "sc-ot8r15-1"
})(["", ""], userHeaderAvatarContainerCss);

var _StyledDiv3$1 = _styled("div").withConfig({
  displayName: "PlateCommentHeader___StyledDiv3",
  componentId: "sc-ot8r15-2"
})(["", ""], userHeaderAssignedToContainerCss);

var _StyledDiv4$1 = _styled("div").withConfig({
  displayName: "PlateCommentHeader___StyledDiv4",
  componentId: "sc-ot8r15-3"
})(["", ""], userHeaderAssignedToTextCss);

var _StyledCommentUserName = _styled(CommentUserName).withConfig({
  displayName: "PlateCommentHeader___StyledCommentUserName",
  componentId: "sc-ot8r15-4"
})(["", ""], commentUserCss);

var _StyledDiv5 = _styled("div").withConfig({
  displayName: "PlateCommentHeader___StyledDiv5",
  componentId: "sc-ot8r15-5"
})(["", ""], userHeaderActionsCss);

const PlateCommentLeaf = props => {
  const {
    children,
    nodeProps,
    leaf
  } = props;
  const [commentIds, setCommentIds] = useState([]);
  const activeCommentId = useCommentsSelectors().activeCommentId();
  const setActiveCommentId = useCommentsActions().activeCommentId();
  const comments = useCommentsSelectors().comments();
  const [commentCount, setCommentCount] = useState(1);
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    const ids = [];
    let count = 0;
    let _isActive = false;
    Object.keys(leaf).forEach(key => {
      var _comments$id;

      if (!isCommentKey(key)) return;
      const id = getCommentKeyId(key);
      if ((_comments$id = comments[id]) !== null && _comments$id !== void 0 && _comments$id.isResolved) return;

      if (id === activeCommentId) {
        _isActive = true;
        setIsActive(true);
      }

      ids.push(getCommentKeyId(key));
      count++;
    });

    if (!_isActive && isActive) {
      setIsActive(false);
    }

    setCommentCount(count);
    setCommentIds(ids);
  }, [activeCommentId, comments, isActive, leaf]);
  const lastCommentId = commentIds[commentIds.length - 1];
  let aboveChildren = /*#__PURE__*/React.createElement(React.Fragment, null, children);
  const backgroundColor = isActive ? 'rgb(255, 212, 0)' : 'rgba(255, 212, 0, 0.14)';

  if (!isActive) {
    for (let i = 1; i < commentCount; i++) {
      aboveChildren = /*#__PURE__*/React.createElement("span", {
        style: {
          backgroundColor: 'rgba(255, 212, 0, 0.14)'
        }
      }, aboveChildren);
    }
  } // hide resolved comments


  if (!commentCount) return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  return /*#__PURE__*/React.createElement(StyledLeaf, _extends({}, props, {
    nodeProps: {
      onMouseDown: e => {
        e.stopPropagation();
        setActiveCommentId(lastCommentId);
      },
      style: {
        backgroundColor,
        borderBottom: '2px solid rgb(255, 212, 0)'
      },
      ...nodeProps
    }
  }), aboveChildren);
};

const PlateCommentNewSubmitButton = () => {
  return /*#__PURE__*/React.createElement(_StyledCommentNewSubmitButton, null);
};

var _StyledCommentNewSubmitButton = _styled(CommentNewSubmitButton).withConfig({
  displayName: "PlateCommentNewSubmitButton___StyledCommentNewSubmitButton",
  componentId: "sc-1rqwru4-0"
})(["", ""], primaryButtonCss);

const commentActionsCss = css(["", ";-webkit-tap-highlight-color:rgba(0,0,0,0);user-select:text;zoom:1;"], {
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "whiteSpace": "normal",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "textAlign": "left",
  "display": "block"
});
const commentFormCss = css(["", ";font-weight:normal;outline:none;"], {
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgba(255, 255, 255, var(--tw-bg-opacity))",
  "cursor": "default",
  "display": "block",
  "fontSize": "0.875rem",
  "lineHeight": "1.25rem",
  "position": "relative",
  "textAlign": "left",
  "--tw-text-opacity": "1",
  "color": "rgba(0, 0, 0, var(--tw-text-opacity))",
  "whiteSpace": "normal"
});
const PlateCommentNewForm = () => {
  const myUserId = useCommentsSelectors().myUserId();
  const textareaRef = useRef(null);
  return /*#__PURE__*/React.createElement(_StyledDiv$2, {
    $_css: [commentFormCss]
  }, /*#__PURE__*/React.createElement(_StyledDiv2$1, null, /*#__PURE__*/React.createElement(PlateAvatar, {
    userId: myUserId
  }), /*#__PURE__*/React.createElement(_StyledDiv3, null, /*#__PURE__*/React.createElement(PlateCommentNewTextarea, {
    ref: textareaRef
  }), /*#__PURE__*/React.createElement(_StyledDiv4, null, /*#__PURE__*/React.createElement(PlateCommentNewSubmitButton, null)))));
};

var _StyledDiv$2 = _styled("div").withConfig({
  displayName: "PlateCommentNewForm___StyledDiv",
  componentId: "sc-cfvod8-0"
})(["", ""], p => p.$_css);

var _StyledDiv2$1 = _styled("div").withConfig({
  displayName: "PlateCommentNewForm___StyledDiv2",
  componentId: "sc-cfvod8-1"
})({
  "display": "flex",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-x-reverse": 0,
    "marginRight": "calc(0.5rem * var(--tw-space-x-reverse))",
    "marginLeft": "calc(0.5rem * calc(1 - var(--tw-space-x-reverse)))"
  },
  "width": "100%"
});

var _StyledDiv3 = _styled("div").withConfig({
  displayName: "PlateCommentNewForm___StyledDiv3",
  componentId: "sc-cfvod8-2"
})({
  "display": "flex",
  "flexDirection": "column",
  "flexGrow": "1",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": 0,
    "marginTop": "calc(0.5rem * calc(1 - var(--tw-space-y-reverse)))",
    "marginBottom": "calc(0.5rem * var(--tw-space-y-reverse))"
  }
});

var _StyledDiv4 = _styled("div").withConfig({
  displayName: "PlateCommentNewForm___StyledDiv4",
  componentId: "sc-cfvod8-3"
})(["", ""], commentActionsCss);

const PlateCommentReplies = () => {
  const commentReplies = useCommentReplies(SCOPE_ACTIVE_COMMENT);
  return /*#__PURE__*/React.createElement(React.Fragment, null, Object.keys(commentReplies).map(id => /*#__PURE__*/React.createElement(PlateComment, {
    key: id,
    commentId: id
  })));
};

const PlateCommentToolbarButton = props => {
  const buttonProps = useCommentAddButton(props);
  return /*#__PURE__*/React.createElement(ToolbarButton, buttonProps);
};

const resolvedCommentsRootCss = css(["", ""], {
  "width": "500px"
});
const resolvedCommentsHeaderCss = css(["", ";border-bottom:1px solid rgb(218,220,224);"], {
  "padding": "1rem",
  "flex": "none",
  "fontWeight": "500",
  "fontSize": "1rem",
  "lineHeight": "1.5rem",
  "marginTop": "0px",
  "marginBottom": "0px"
});
const resolvedCommentsBodyCss = css(["", ";& > *{", ";}& > *:last-child{", ";}"], {
  "padding": "1rem",
  "overflowY": "auto",
  "flex": "1 1 auto"
}, {
  "marginBottom": "1rem"
}, {
  "marginBottom": "0px"
});
const PlateCommentsResolved = () => {
  const resolvedComments = useCommentsResolved();
  return /*#__PURE__*/React.createElement(_StyledDiv$1, null, /*#__PURE__*/React.createElement(_StyledH, null, "Resolved comments"), /*#__PURE__*/React.createElement(_StyledDiv2, null, resolvedComments.map(comment => /*#__PURE__*/React.createElement(PlateComment, {
    key: comment.id,
    commentId: comment.id
  }))));
};

var _StyledDiv$1 = _styled("div").withConfig({
  displayName: "PlateCommentsResolved___StyledDiv",
  componentId: "sc-1s2jxlr-0"
})(["", ""], resolvedCommentsRootCss);

var _StyledH = _styled("h2").withConfig({
  displayName: "PlateCommentsResolved___StyledH",
  componentId: "sc-1s2jxlr-1"
})(["", ""], resolvedCommentsHeaderCss);

var _StyledDiv2 = _styled("div").withConfig({
  displayName: "PlateCommentsResolved___StyledDiv2",
  componentId: "sc-1s2jxlr-2"
})(["", ""], resolvedCommentsBodyCss);

const PlateCommentsShowResolvedButton = props => {
  const {
    id,
    fetchContacts,
    renderContainer,
    retrieveUser,
    ...otherProps
  } = props;
  const editor = usePlateEditorRef(useEventPlateId(id));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isActive = Boolean(anchorEl);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ToolbarButton, _extends({
    "aria-label": "Show resolved comments",
    active: isActive,
    onClick: e => {
      e.preventDefault();
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      focusEditor(editor);
    }
  }, otherProps)));
};

const commentsRootCss = css(["", ";box-shadow:0 2px 6px 2px rgb(60 64 67 / 15%);border:1px solid white;"], {
  "borderRadius": "0.5rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgba(255, 255, 255, var(--tw-bg-opacity))",
  "padding": "0.75rem",
  "display": "flex",
  "flexDirection": "column",
  "> :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": 0,
    "marginTop": "calc(0.5rem * calc(1 - var(--tw-space-y-reverse)))",
    "marginBottom": "calc(0.5rem * var(--tw-space-y-reverse))"
  }
});
const PlateFloatingCommentsContent = props => {
  const {
    disableForm
  } = props;
  const {
    ref,
    activeCommentId,
    hasNoComment,
    myUserId
  } = useFloatingCommentsContentState();
  return /*#__PURE__*/React.createElement(CommentProvider, {
    key: activeCommentId,
    id: activeCommentId,
    scope: SCOPE_ACTIVE_COMMENT
  }, /*#__PURE__*/React.createElement(_StyledDiv, {
    ref: ref
  }, !hasNoComment && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PlateComment, {
    key: activeCommentId,
    commentId: activeCommentId
  }), /*#__PURE__*/React.createElement(PlateCommentReplies, null)), !!myUserId && !disableForm && /*#__PURE__*/React.createElement(PlateCommentNewForm, null)));
};

var _StyledDiv = _styled("div").withConfig({
  displayName: "PlateFloatingCommentsContent___StyledDiv",
  componentId: "sc-15312b8-0"
})(["", ""], commentsRootCss);

const floatingCommentsRootCss = css(["", ""], {
  "position": "absolute",
  "zIndex": "10",
  "paddingBottom": "1rem",
  "width": "418px"
});
const PlateFloatingComments = () => {
  const {
    loaded,
    activeCommentId
  } = useFloatingCommentsState();
  if (!loaded || !activeCommentId) return null;
  return /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement(_StyledCommentsPositioner, null, /*#__PURE__*/React.createElement(PlateFloatingCommentsContent, null)));
};

var _StyledCommentsPositioner = _styled(CommentsPositioner).withConfig({
  displayName: "PlateFloatingComments___StyledCommentsPositioner",
  componentId: "sc-d3zj8h-0"
})(["", ""], floatingCommentsRootCss);

export { PlateAvatar, PlateComment, PlateCommentEditTextarea, PlateCommentHeader, PlateCommentLeaf, PlateCommentMenuButton, PlateCommentNewForm, PlateCommentNewSubmitButton, PlateCommentNewTextarea, PlateCommentReplies, PlateCommentResolveButton, PlateCommentToolbarButton, PlateCommentValue, PlateCommentsResolved, PlateCommentsShowResolvedButton, PlateFloatingComments, PlateFloatingCommentsContent, avatarAccountCircleCss, avatarImageCss, avatarRootCss, commentActionsCss, commentFormCss, commentHeaderCreatedDateCss, commentHeaderInfoCss, commentHeaderUserNameCss, commentTextCss, commentTextareaCss, commentUserCss, commentsHeaderCss, commentsRootCss, floatingCommentsRootCss, resolvedCommentsBodyCss, resolvedCommentsHeaderCss, resolvedCommentsRootCss, userHeaderActionsCss, userHeaderAssignedToContainerCss, userHeaderAssignedToTextCss, userHeaderAvatarContainerCss, userHeaderRootCss };
//# sourceMappingURL=index.es.js.map
